import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { AlertCircle, Loader2, Ticket } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import RyftPay3DSDialog from './RyftPay3DSDialog';
import { apiGet, apiPost } from '../api';
import { recordBreadcrumb, reportError } from '../lib/sentry';
import { formatApiError } from '../lib/apiError';

/**
 * Shared booking dialog used by both <SchedulePage /> and <StudioDetailPage />.
 *
 * Encapsulates:
 *   1. The "Choose Payment Method" selection screen (ClassPass vs Pay Single Class).
 *   2. POST /api/bookings with the correct payment-intent flags so the strict
 *      security guard added in /api/bookings doesn't 400 the request.
 *   3. The RyftPay 3DS card-form flow when the studio charges for the class.
 *
 * Props:
 *   bookingClass        – class object (or null to keep dialog closed)
 *   onClose             – called when user cancels or the flow completes
 *   onBooked(cls)       – success callback (caller refreshes its own data)
 *   navigateToStudio    – optional fn invoked when the user clicks "Buy a ClassPass"
 *                         (defaults to navigating to /studios/<slug or id>)
 */
export const ClassBookingDialog = ({ bookingClass, onClose, onBooked, navigateToStudio }) => {
  const navigate = useNavigate();
  const [classPasses, setClassPasses] = useState([]);
  const [selectedPassId, setSelectedPassId] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedSavedCard, setSelectedSavedCard] = useState('');
  const [publicKey, setPublicKey] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);

  const [show3DS, setShow3DS] = useState(false);
  const [threeDSData, setThreeDSData] = useState(null);

  // Load user's class passes + saved cards + public key whenever a class is selected
  useEffect(() => {
    if (!bookingClass) return;
    let cancelled = false;
    (async () => {
      try {
        const [passes, cards, cfg] = await Promise.all([
          apiGet('/api/user-class-passes').catch(() => []),
          apiGet('/api/payment-methods').catch(() => []),
          apiGet('/api/payments/config').catch(() => ({})),
        ]);
        if (cancelled) return;
        setClassPasses(Array.isArray(passes) ? passes.filter(p => p.status === 'active') : []);
        setSavedCards(Array.isArray(cards) ? cards : []);
        const defaultCard = cards?.find(c => c.is_default) || cards?.[0];
        if (defaultCard) setSelectedSavedCard(defaultCard.id);
        if (cfg?.public_key) setPublicKey(cfg.public_key);
      } catch (e) {
        console.error('ClassBookingDialog: failed to load context', e);
      }
    })();
    setSelectedPassId(null);
    return () => { cancelled = true; };
  }, [bookingClass]);

  const getApplicablePasses = useCallback(() => {
    if (!bookingClass) return [];
    const activity = bookingClass.activity_type || 'general';
    return classPasses.filter(p =>
      p.studio_id === bookingClass.studio_id &&
      (p.activity_type === activity || p.activity_type === 'general') &&
      p.passes_remaining > 0
    );
  }, [bookingClass, classPasses]);

  const closeAll = () => {
    setShow3DS(false);
    setThreeDSData(null);
    setSelectedPassId(null);
    onClose?.();
  };

  const confirmBooking = async () => {
    if (!bookingClass) return;
    const useClassPass = selectedPassId !== null;
    const paySingleClass = !useClassPass && bookingClass.price > 0;

    setBookingLoading(true);
    recordBreadcrumb('booking', 'User confirming class booking', {
      class_id: bookingClass.id,
      studio_id: bookingClass.studio_id,
      class_name: bookingClass.name,
      use_class_pass: useClassPass,
      class_pass_id: useClassPass ? selectedPassId : null,
      pay_single_class: paySingleClass,
    });

    try {
      const body = {
        class_id: bookingClass.id,
        studio_id: bookingClass.studio_id,
        use_class_pass: useClassPass,
        class_pass_id: useClassPass ? selectedPassId : null,
        pay_single_class: paySingleClass,
        payment_method_id: paySingleClass ? (selectedSavedCard || null) : null,
      };

      const response = await apiPost('/api/bookings', { body });

      // Paid class — 3DS required
      if (response.status === 'requires_action' && response.client_secret) {
        let customerPaymentMethods = [];
        try {
          customerPaymentMethods = await apiGet('/api/payments/customer/methods');
        } catch (_) { /* fall through to plain card-entry */ }

        setThreeDSData({
          clientSecret: response.client_secret,
          pendingBookingId: response.pending_booking_id,
          paymentSessionId: response.payment_session_id,
          redirectUrl: response.redirect_url,
          accountId: response.account_id,
          // Iter-46ar: backend signals when the session requires a saved card.
          // Single class bookings are paymentType=Standard → save is optional.
          requireSaveCard: !!response.require_save_card,
          amountLabel: `€${Number(bookingClass.price).toFixed(2)} — ${bookingClass.name}`,
          customerPaymentMethods,
        });
        setShow3DS(true);
        setBookingLoading(false);
        return;
      }

      // Free booking or pass-redemption — completed immediately
      toast.success('Class booked successfully!');
      onBooked?.(bookingClass);
      closeAll();
    } catch (e) {
      reportError(e, {
        flow: 'class_booking',
        action: 'confirm',
        extra: {
          class_id: bookingClass?.id,
          studio_id: bookingClass?.studio_id,
          use_class_pass: useClassPass,
          pay_single_class: paySingleClass,
        },
      });
      toast.error(formatApiError(e, 'Failed to book class'));
    } finally {
      setBookingLoading(false);
    }
  };

  const handle3DSSuccess = async (result) => {
    try {
      const sessionId = result?.paymentSession?.id;
      if (sessionId) {
        apiPost('/api/payment-methods/sync-from-session', { body: { payment_session_id: sessionId } })
          .catch((err) => console.warn('save-card sync skipped:', err?.message || err));
      }
      await apiPost('/api/bookings/confirm-3ds', { query: { pending_booking_id: threeDSData.pendingBookingId } });
      toast.success('Payment successful! Class booked!');
      onBooked?.(bookingClass);
      closeAll();
    } catch (e) {
      reportError(e, {
        flow: 'class_booking',
        action: 'confirm_3ds',
        extra: { pending_booking_id: threeDSData?.pendingBookingId },
      });
      toast.error(formatApiError(e, 'Failed to confirm booking'));
    }
  };

  if (!bookingClass) return null;

  const applicablePasses = getApplicablePasses();
  const buyPassFallback = () => {
    if (navigateToStudio) {
      navigateToStudio(bookingClass);
      return;
    }
    // Bug #6 fix: SPA route is /studio/:slug (singular). Old /studios/{slug}
    // path fell through the catch-all Route and bounced the user to home.
    const target = bookingClass.studio_slug || bookingClass.studio_id;
    if (target) navigate(`/studio/${target}?tab=classpasses`);
  };

  return (
    <>
      {/* Selection dialog */}
      {!show3DS && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="booking-dialog"
        >
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Book Class</CardTitle>
              <CardDescription>
                {bookingClass.name} - {format(new Date(bookingClass.start_time), 'h:mm a')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 rounded-lg bg-muted">
                <p className="font-medium">{bookingClass.name}</p>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(bookingClass.start_time), 'EEEE, MMMM d')} at {format(new Date(bookingClass.start_time), 'h:mm a')}
                </p>
                {bookingClass.studio_name && (
                  <p className="text-sm text-muted-foreground">{bookingClass.studio_name}</p>
                )}
              </div>

              <div className="space-y-3">
                <Label className="text-base font-medium">Choose Payment Method:</Label>

                {applicablePasses.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Ticket className="w-4 h-4" />
                      Use a ClassPass:
                    </p>
                    {applicablePasses.map((pass) => (
                      <div
                        key={pass.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedPassId === pass.id
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                            : 'hover:border-muted-foreground'
                        }`}
                        onClick={() => setSelectedPassId(selectedPassId === pass.id ? null : pass.id)}
                        data-testid={`select-pass-${pass.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{pass.class_pass_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {pass.passes_remaining} passes remaining
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {selectedPassId === pass.id && (
                              <Badge className="bg-primary">Selected</Badge>
                            )}
                            <Ticket className="w-5 h-5 text-primary" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {bookingClass.price > 0 && (
                  <div className="space-y-3">
                    <div
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedPassId === null
                          ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                          : 'hover:border-muted-foreground'
                      }`}
                      onClick={() => setSelectedPassId(null)}
                      data-testid="pay-single-rate"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Pay Single Class Rate</p>
                          <p className="text-sm text-muted-foreground">One-time drop-in payment</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedPassId === null && (
                            <Badge className="bg-primary">Selected</Badge>
                          )}
                          <span className="text-xl font-bold text-primary">€{bookingClass.price}</span>
                        </div>
                      </div>
                    </div>

                    {selectedPassId === null && (
                      <div className="ml-4 p-3 rounded-lg border border-dashed bg-muted/30">
                        <p className="text-sm text-muted-foreground">
                          {savedCards.length > 0
                            ? "You'll confirm payment with your saved card on the next step."
                            : "You'll be prompted to add a payment card on the next step."}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {applicablePasses.length === 0 && bookingClass.price > 0 && (
                  <div className="p-4 rounded-lg border border-dashed border-amber-300 bg-amber-50">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-800">No ClassPass on file for this class</p>
                        <p className="text-xs text-amber-700">
                          Pay the single-class rate above, or browse this studio&apos;s ClassPasses to save on future bookings.
                        </p>
                      </div>
                    </div>
                    {(bookingClass.studio_slug || bookingClass.studio_id) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 w-full border-amber-400 text-amber-700 hover:bg-amber-100"
                        onClick={buyPassFallback}
                        data-testid="buy-classpass-btn"
                      >
                        <Ticket className="w-4 h-4 mr-2" />
                        Browse this studio&apos;s ClassPasses
                      </Button>
                    )}
                  </div>
                )}

                {bookingClass.price === 0 && applicablePasses.length === 0 && (
                  <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <p className="text-sm text-emerald-700 font-medium">This is a free class!</p>
                    <p className="text-xs text-emerald-600">No payment required</p>
                  </div>
                )}
              </div>
            </CardContent>
            <div className="flex justify-end gap-2 p-6 pt-0">
              <Button variant="outline" onClick={closeAll} data-testid="cancel-booking-dialog">
                Cancel
              </Button>
              <Button
                onClick={confirmBooking}
                disabled={bookingLoading}
                data-testid="confirm-booking-btn"
              >
                {bookingLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {selectedPassId
                  ? 'Use ClassPass'
                  : bookingClass.price > 0
                    ? `Pay €${bookingClass.price}`
                    : 'Confirm Booking'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* 3DS payment dialog (only mounted while paying) */}
      <RyftPay3DSDialog
        open={show3DS}
        onClose={() => { setShow3DS(false); setThreeDSData(null); toast.info('Payment cancelled'); }}
        publicKey={publicKey}
        clientSecret={threeDSData?.clientSecret}
        accountId={threeDSData?.accountId}
        redirectUrl={threeDSData?.redirectUrl}
        amountLabel={threeDSData?.amountLabel}
        customerPaymentMethods={threeDSData?.customerPaymentMethods}
        requireSaveCard={threeDSData?.requireSaveCard}
        onSuccess={handle3DSSuccess}
      />
    </>
  );
};

export default ClassBookingDialog;
